import {
  Download,
  InfoOutlined,
  OpenInNew,
  Plagiarism,
  Warning,
  WarningAmber
} from '@mui/icons-material';
import { Tooltip, IconButton } from '@mui/material';
import { Stack } from '@mui/system';
import { GridActionsCellItem, GridRowParams } from '@mui/x-data-grid';
import { Invoice, InvoiceCSV } from '@thro-types/billing';

import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { TypedGridColumns } from 'app/components';
import { handleDownload } from 'app/components/Util/downloadAttachement';
import { translations } from 'locales/i18n';
import { countries, countryToFlag } from 'utils/Countries';
import { formatMonetaryValue } from 'utils/formatMonetaryValue';

import { InvoiceStateChip } from '.';
import {
  DATEFORMAT,
  DATEFORMATWITHTIME,
  getDistinctTaxRates,
  hasArticleWithChangedDefaultTax,
  hasArticleWithTaxNotZero,
  hasFreeTextArticle,
  isForeignCustomer
} from '../utils';

interface Props {
  handlePdfPreview?: (invoice: Invoice) => Promise<void> | undefined;
  disableStateSorting?: boolean;
}

export const useFinanceInvoiceColumns = (props?: Props) => {
  const { t } = useTranslation();

  function limitShownChars(text: string): string {
    return text.length > 60 ? `${text.slice(0, 60)}...` : text;
  }

  return useMemo((): TypedGridColumns<Invoice & { taxRate?: string }, 'pdfPreview'> => {
    return [
      {
        field: 'creator.uid',
        flex: 1.2,
        headerName: t(translations.pages.billing.creator()),
        renderCell: ({ row }) => row.creator?.uid || t(translations.common.unavailable()),
        disableColumnMenu: true
      },
      {
        field: 'outgoingInvoiceNumber',
        flex: 1.5,
        headerName: t(translations.pages.billing.invoiceNumber()),
        renderCell: params =>
          params.value || t(translations.pages.billing.noInvoiceNumberInDraftState())
      },
      {
        field: 'bookingCode',
        flex: 1.5,
        headerName: t(translations.pages.billing.bookingCode()),
        renderCell: params =>
          params.value || t(translations.pages.billing.noBookingCodeInDraftState())
      },
      {
        field: 'customer',
        flex: 2,
        headerName: t(translations.common.customer()),
        renderCell: ({ row }) => {
          const name = `${
            row.customer?.companyName ||
            [row.customer?.firstName, row.customer?.lastName].filter(Boolean).join(' ')
          }  ${row.state === 'FINAL' ? '\n' + row.customer?.email || '' : ''}`;

          return (
            <Tooltip placement="top-start" title={name}>
              <div style={{ whiteSpace: 'pre-line' }}>{limitShownChars(name)}</div>
            </Tooltip>
          );
        }
      },
      {
        field: 'totalNet',
        flex: 1,
        headerName: t(translations.common.netto()),
        renderCell: params => formatMonetaryValue(params.value || 0)
      },
      {
        field: 'totalGross',
        flex: 1,
        headerName: t(translations.common.brutto()),
        renderCell: params => formatMonetaryValue(params.value || 0)
      },
      {
        field: 'taxRate',
        flex: 0.8,
        headerName: t(translations.pages.billingSettings.tax.taxRate()),
        headerAlign: 'center',
        sortable: false,
        valueGetter: (params, row) => row.invoiceArticles.map(ia => ia.salestax?.taxRate)[0],
        renderCell: params => (
          <Stack
            flex={1}
            // direction={'column'}
            alignItems={'flex-end'}
            justifyContent={'flex-end'}
            gap={0.5}
          >
            <Stack gap={1} direction={'row'} alignItems={'center'}>
              {getDistinctTaxRates(params.row).length > 1 && (
                <Tooltip title={t(translations.pages.billing.tooltips.moreThanOneTax())}>
                  <WarningAmber />
                </Tooltip>
              )}
              {hasArticleWithChangedDefaultTax(params.row) && (
                <Tooltip title={t(translations.pages.billing.tooltips.defaultTaxChanged())}>
                  <Warning color="error" />
                </Tooltip>
              )}
              {hasFreeTextArticle(params.row) && (
                <Tooltip title={t(translations.pages.billing.tooltips.hasFreeTextArticle())}>
                  <InfoOutlined color="info" />
                </Tooltip>
              )}
              {isForeignCustomer(params.row.customer!) && hasArticleWithTaxNotZero(params.row) && (
                <Tooltip title={t(translations.pages.billing.tooltips.foreignTaxNotNull())}>
                  <Warning color="error" />
                </Tooltip>
              )}
            </Stack>
            {getDistinctTaxRates(params.row).join(', ')}
          </Stack>
        )
      },
      {
        field: 'customer.country',
        flex: 0.6,
        headerName: t(translations.common.country()),
        valueGetter: (_, row) => row.customer?.country,
        renderCell: ({ row }) => (
          <>
            <span style={{ marginRight: '0.5em' }}>
              {countryToFlag(countries.find(c => c.code === row.customer?.country)?.code || '')}
            </span>
            {row.customer?.country}
          </>
        )
      },
      {
        field: 'invoiceDate',
        flex: 1,
        headerName: t(translations.pages.billing.invoiceCreate.invoiceDate()),
        renderCell: params => format(new Date(params.value), DATEFORMAT, { locale: de })
      },
      {
        field: 'approvedAt',
        flex: 1,
        headerName: t(translations.pages.billingFinance.exportDate()),
        renderCell: params =>
          params.value ? format(new Date(params.value), DATEFORMAT, { locale: de }) : undefined
      },
      {
        field: 'state',
        flex: 1,
        headerName: t(translations.common.state()),
        renderCell: params => (
          <InvoiceStateChip state={params.value} cancelled={!!params.row.cancelledInvoiceId} />
        ),
        align: 'center',
        headerAlign: 'center',
        sortable: !props?.disableStateSorting
      },
      {
        field: 'actions',
        type: 'actions',
        flex: 1,
        headerName: t(translations.common.actions()),
        renderCell: params =>
          params.row.dmsLink && (
            <Link to={params.row.dmsLink} target="_blank" style={{ cursor: 'pointer' }}>
              <GridActionsCellItem
                icon={<OpenInNew />}
                label={t(translations.pages.billingFinance.openInDMS())}
              />
            </Link>
          ),
        align: 'center',
        headerAlign: 'center',
        sortable: false
      },
      {
        field: 'pdfPreview',
        flex: 0.7,
        headerName: t(translations.common.preview()),
        renderCell: params => (
          <IconButton
            onClick={async e => {
              e.stopPropagation();
              props?.handlePdfPreview && props.handlePdfPreview(params.row);
            }}
          >
            <Plagiarism />
          </IconButton>
        ),
        align: 'center',
        headerAlign: 'center',
        sortable: false
      }
    ];
  }, [t]);
};

export const useCSVColumns = () => {
  const { t } = useTranslation();

  const rowActions = useCallback(
    (params: GridRowParams<InvoiceCSV>) => {
      return [
        <GridActionsCellItem
          icon={<Download />}
          label={t(translations.common.download())}
          onClick={() => {
            handleDownload(params.row.fileName, `/billing/v1/exports/${params.row.id}`);
          }}
        />,
        params.row.dmsLink ? (
          <Link to={params.row.dmsLink} target="_blank">
            <GridActionsCellItem
              icon={<OpenInNew />}
              label={t(translations.pages.billingFinance.openInDMS())}
            />
          </Link>
        ) : (
          <></>
        )
      ];
    },
    [t]
  );

  return useMemo((): TypedGridColumns<
    InvoiceCSV & { invoiceIds: string[] } & { exportUserUID: string }
  > => {
    return [
      {
        field: 'fileName',
        flex: 1,
        headerName: t(translations.pages.billingFinance.fileName()),
        renderCell: params => params.value
      },
      {
        field: 'exportUserUID',
        flex: 1,
        headerName: t(translations.pages.billingFinance.exportUser()),
        renderCell: ({ row }) =>
          row.exportUserUID
            ? row.exportUserUID
            : t(translations.pages.billingFinance.unknownExportUser())
      },
      {
        field: 'createdAt',
        flex: 0.5,
        headerName: t(translations.pages.billingFinance.exportDate()),
        renderCell: params =>
          params.value
            ? format(new Date(params.value), DATEFORMATWITHTIME, { locale: de })
            : undefined
      },
      {
        field: 'actions',
        flex: 0.5,
        headerName: t(translations.common.actions()),
        type: 'actions',
        getActions: rowActions
      }
    ];
  }, [rowActions, t]);
};
